import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { apiData } from "../services/api_service";
import { stockTransferDataAPI, reservedStockDetailsAPI } from "../config";
import { formatNumber } from "../utils/format";

// Drill-down for the "In Stock" figure on the Stock Inventory Report. The
// physical stock is held in batches (each with its own manufacture date +
// expiry), and this lists them. It also surfaces how much of the total is
// Reserved for pending orders — which is exactly why Stock Adjustment lets you
// deduct LESS than the In Stock number: reserved units are protected.
const SummaryStat = ({ label, value, color }) => (
  <Box>
    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{label}</Typography>
    <Typography sx={{ fontSize: 18, fontWeight: 700, color: color || "inherit" }}>
      {formatNumber(value)}
    </Typography>
  </Box>
);

const BatchSplitModal = ({
  open,
  onClose,
  productId,
  storeId,
  productName,
  storeName,
}) => {
  const [batches, setBatches] = useState([]);
  const [reserved, setReserved] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !productId || !storeId) return;
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [stockRes, resRes] = await Promise.all([
          apiData(
            "get",
            `${stockTransferDataAPI}?product_id=${productId}&store_id=${storeId}`
          ),
          apiData(
            "get",
            `${reservedStockDetailsAPI}?product_id=${productId}&store_id=${storeId}`
          ),
        ]);

        const rows =
          stockRes.success && Array.isArray(stockRes.data) ? stockRes.data : [];
        // Group by manufacture date + expiry days, summing physical stock —
        // same grouping the backend uses to match a deduct.
        const grouped = Object.values(
          rows.reduce((acc, item) => {
            const key = `${item.manufacture_date}|${item.life_days}`;
            if (!acc[key]) {
              acc[key] = {
                manufacture_date: item.manufacture_date,
                life_days: item.life_days,
                qty: 0,
              };
            }
            acc[key].qty += Number(item.stock_count || 0);
            return acc;
          }, {})
        )
          .filter((b) => Number(b.qty) > 0)
          .map((b) => {
            // Matches the backend: expiry = manufacture date + (life_days - 1).
            const expiry =
              b.manufacture_date && Number(b.life_days) > 0
                ? moment(b.manufacture_date).add(Number(b.life_days) - 1, "days")
                : null;
            return {
              ...b,
              expiry,
              expired: expiry ? expiry.isBefore(moment(), "day") : false,
            };
          })
          .sort((a, b) =>
            a.expiry && b.expiry ? a.expiry.valueOf() - b.expiry.valueOf() : 0
          );
        setBatches(grouped);

        const resRows =
          resRes.success && Array.isArray(resRes.data) ? resRes.data : [];
        setReserved(resRows.reduce((s, r) => s + Number(r.quantity || 0), 0));
      } catch (e) {
        console.error("Failed to fetch batch split:", e);
        setBatches([]);
        setReserved(0);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [open, productId, storeId]);

  const totalInStock = batches.reduce((s, b) => s + Number(b.qty || 0), 0);
  const totalExpired = batches.reduce(
    (s, b) => s + (b.expired ? Number(b.qty || 0) : 0),
    0
  );
  const freeToDeduct = Math.max(0, totalInStock - reserved);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            In Stock — Batch Split
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {productName || "Product"} {storeName ? `· ${storeName}` : ""}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : batches.length === 0 ? (
          <Typography
            sx={{ py: 3, textAlign: "center", color: "text.secondary" }}
          >
            No stock in this store for this product.
          </Typography>
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Manufacture Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expiry Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Quantity
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batches.map((b, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        {b.manufacture_date
                          ? moment(b.manufacture_date).format("DD-MM-YYYY")
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {b.expiry ? b.expiry.format("DD-MM-YYYY") : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={b.expired ? "EXPIRED" : "OK"}
                          size="small"
                          sx={{
                            backgroundColor: b.expired ? "#fdecea" : "#e8f5e9",
                            color: b.expired ? "#be123c" : "#2e7d32",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatNumber(b.qty)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="right"
                      sx={{ fontWeight: 600 }}
                    >
                      Total In Stock
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatNumber(totalInStock)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            <Box sx={{ mt: 2.5, display: "flex", gap: 4, flexWrap: "wrap" }}>
              <SummaryStat label="In Stock (physical)" value={totalInStock} />
              <SummaryStat
                label="Reserved (protected)"
                value={reserved}
                color="#c62828"
              />
              {totalExpired > 0 && (
                <SummaryStat
                  label="Expired"
                  value={totalExpired}
                  color="#be123c"
                />
              )}
              <SummaryStat
                label="Free to deduct"
                value={freeToDeduct}
                color="#2e7d32"
              />
            </Box>

            {reserved > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {formatNumber(reserved)} unit(s) are reserved for pending
                orders/CDNs. They stay in stock but are committed, so Stock
                Adjustment lets you deduct only {formatNumber(freeToDeduct)}{" "}
                (In Stock − Reserved).
              </Alert>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BatchSplitModal;
